import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType,
  ogUrl,
  ogTitle,
  ogDescription,
  ogImage,
  twitterCard,
  twitterTitle,
  twitterDescription,
  twitterImage,
  schema,
}: SEOProps) => {
  useEffect(() => {
    const setMeta = (selector: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute('content', value);
    };
    const setMetaProp = (property: string, value: string) => {
      const el = document.querySelector(`meta[property="${property}"]`);
      if (el) el.setAttribute('content', value);
    };

    if (title) {
      document.title = title;
      setMeta('meta[name="title"]', title);
    }
    if (description) setMeta('meta[name="description"]', description);
    if (keywords) setMeta('meta[name="keywords"]', keywords);

    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonical;
    }

    // Open Graph
    if (ogType) setMetaProp('og:type', ogType);
    if (ogUrl) setMetaProp('og:url', ogUrl);
    if (ogTitle) setMetaProp('og:title', ogTitle);
    if (ogDescription) setMetaProp('og:description', ogDescription);
    if (ogImage) setMetaProp('og:image', ogImage);

    // Twitter / X Card
    if (twitterCard) setMeta('meta[name="twitter:card"]', twitterCard);
    if (twitterTitle) {
      const el =
        document.querySelector('meta[name="twitter:title"]') ||
        document.querySelector('meta[property="twitter:title"]');
      if (el) el.setAttribute('content', twitterTitle);
    }
    if (twitterDescription) {
      const el =
        document.querySelector('meta[name="twitter:description"]') ||
        document.querySelector('meta[property="twitter:description"]');
      if (el) el.setAttribute('content', twitterDescription);
    }
    if (twitterImage) {
      const el =
        document.querySelector('meta[name="twitter:image"]') ||
        document.querySelector('meta[property="twitter:image"]');
      if (el) el.setAttribute('content', twitterImage);
    }

    // last-modified
    const metaLastModified = document.querySelector('meta[name="last-modified"]');
    if (metaLastModified) {
      metaLastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
    }

    // Schema JSON-LD
    if (schema) {
      let schemaScript = document.querySelector('script[data-schema="page"]') as HTMLScriptElement | null;
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.type = 'application/ld+json';
        schemaScript.setAttribute('data-schema', 'page');
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schema);
    }

    return () => {
      const schemaScript = document.querySelector('script[data-schema="page"]');
      if (schemaScript) schemaScript.remove();
    };
  }, [title, description, keywords, canonical, ogType, ogUrl, ogTitle, ogDescription, ogImage, twitterCard, twitterTitle, twitterDescription, twitterImage, schema]);
};
